import { Fragment, memo, useMemo } from 'react'

import { CopyAll, Memory, Lock, LockOpen, DataArray, Difference } from '@mui/icons-material'
import { Box, Button, Paper, styled, SvgIcon, Typography } from '@mui/material'
import {
  AvcInstanceAvioSdi,
  AvcInstanceSdiUtilization,
  AvcInstanceSdiVideoUtilizationFieldOrderEnum,
} from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { Engine } from 'emotion-icons/fluentui-system-filled'
import { useSnackbar } from 'notistack'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { LoadingIndicator, NotFound } from '@shared/layouts'
import { AVIOPanel, Networking } from 'domains/ContentProcessing/AvcInstances/Instance/Dashboard'
import { SdiPanel } from 'domains/ContentProcessing/AvcInstances/Instance/Dashboard/Sdi'
import { dashboardRoute } from 'pages/NWPlatform/AvcSystem/Instances/Instance'
import { avcInstanceQueryKeys } from 'services/queryKeys'

const Panel = styled(Paper)(({ theme }) => ({
  flexGrow: 1,
  flexBasis: 240,
  maxWidth: 'max-content',
  gridRow: 2,
  padding: theme.spacing(2),
  minWidth: 240,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, auto)',
  gridAutoRows: 'max-content auto',
  alignItems: 'flex-start',
  alignContent: 'flex-start',
  justifyContent: 'space-between',
  justifyItems: 'flex-end',
  rowGap: theme.spacing(1),
  columnGap: theme.spacing(4),
}))

const fieldOrder = {
  [AvcInstanceSdiVideoUtilizationFieldOrderEnum.Progressive]: 'p',
  [AvcInstanceSdiVideoUtilizationFieldOrderEnum.BottomCodedBottomDisplayed]: 'i',
  [AvcInstanceSdiVideoUtilizationFieldOrderEnum.TopCodedBottomDisplayed]: 'i',
  [AvcInstanceSdiVideoUtilizationFieldOrderEnum.BottomCodedTopDisplayed]: 'i',
  [AvcInstanceSdiVideoUtilizationFieldOrderEnum.TopCodedTopDisplayed]: 'i',
  [AvcInstanceSdiVideoUtilizationFieldOrderEnum.Unknown]: 'x',
}

const formatFrameRate = (frameRate: string) => {
  const [numerator, denominator] = frameRate.split('/')
  if (denominator) {
    return Math.round((Number(numerator) / Number(denominator)) * 100) / 100
  }
  return numerator
}

const formatSdiDevices = (capableDevices: AvcInstanceAvioSdi[], utilizedDevices: AvcInstanceSdiUtilization[]) => {
  if (!capableDevices?.length) {
    return []
  }
  return capableDevices.map((capableDevice) => {
    const utilizedDevice = utilizedDevices.find((d) => d.id === capableDevice.id)
    const video = utilizedDevice?.video?.[0]
    const audio = utilizedDevice?.audio?.[0]

    return {
      ...capableDevice,
      width: video?.width,
      height: video?.height,
      fieldOrder: video?.fieldOrder,
      frameRate: formatFrameRate(video ? video.frameRate : ''),
      sampleRate: audio?.sampleRate,
      channels: audio?.channels,
      isActive: !!utilizedDevice,
      videoFormat: video
        ? `${video.height}${fieldOrder[video.fieldOrder || 'Unknown']}${formatFrameRate(video.frameRate)}`
        : undefined,
      thumbnail: video?.thumbnail,
      volume: audio?.channels.length
        ? Math.round(
            Math.max(
              ...audio.channels
                .filter((channel) => typeof channel.volume === 'number')
                .map((channel) => channel.volume as number),
            ) * 10,
          ) / 10
        : undefined,
    }
  })
}

const DashboardRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { enqueueSnackbar } = useSnackbar()

  const { avcInstanceId: instanceId } = dashboardRoute.useParams()

  const { data: avcInstance, isLoading } = useQuery({
    ...avcInstanceQueryKeys.status({ instanceId }),
    refetchInterval: 5000,
  })

  const sdiDevices = useMemo(() => {
    const utilizedDevices = avcInstance?.utilization?.hardware.sdi || []
    const capableDevices = avcInstance?.capabilities?.avio.sdi || []

    return formatSdiDevices(capableDevices, utilizedDevices)
  }, [avcInstance?.utilization?.hardware.sdi, avcInstance?.capabilities?.avio.sdi])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!avcInstance) {
    return <NotFound />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="heading-lg">{t('domain:ContentProcessing.Instance.hardwareInformation')}</Typography>

      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Panel elevation={0}>
          <Typography sx={{ mb: 2, alignSelf: 'flex-start', justifySelf: 'flex-start' }} variant="heading-md">
            {t('domain:ContentProcessing.Instance.engine')}
          </Typography>
          <SvgIcon sx={{ alignSelf: 'flex-start' }}>
            <Engine />
          </SvgIcon>

          <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
            {`${t('domain:ContentProcessing.Instance.version')}:`}
          </Typography>
          <Typography variant="heading-sm" noWrap>
            {avcInstance.capabilities ? avcInstance.capabilities.engine.version : 'N/A'}
          </Typography>
          <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
            {`${t('domain:ContentProcessing.Instance.type')}:`}
          </Typography>
          <Typography variant="heading-sm" noWrap>
            {avcInstance.capabilities ? avcInstance.capabilities.engine.type : 'N/A'}
          </Typography>
        </Panel>
        <Panel elevation={0}>
          <Typography sx={{ mb: 2, alignSelf: 'flex-start', justifySelf: 'flex-start' }} variant="heading-md">
            {t('domain:ContentProcessing.Instance.cpu')}
          </Typography>
          <Memory sx={{ alignSelf: 'flex-start' }} />

          {avcInstance.capabilities?.hardware.cpu?.length ? (
            avcInstance.capabilities?.hardware.cpu?.map((cpu, idx) => (
              <Fragment key={cpu.id}>
                <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
                  {t('domain:ContentProcessing.Instance.cpu')} {`#${idx + 1}`}:
                </Typography>
                <Typography variant="heading-sm" noWrap>
                  {cpu.model}
                </Typography>
              </Fragment>
            ))
          ) : (
            <Typography>{t('domain:ContentProcessing.Instance.noCpuAvailable')}</Typography>
          )}
        </Panel>
        <Panel elevation={0}>
          <Typography sx={{ mb: 2, alignSelf: 'flex-start', justifySelf: 'flex-start' }} variant="heading-md">
            {t('domain:ContentProcessing.Instance.nVEncoder')}
          </Typography>
          <Lock sx={{ alignSelf: 'flex-start' }} />

          {avcInstance.capabilities?.hardware.nvenc?.length ? (
            avcInstance.capabilities?.hardware.nvenc?.map((enc, idx) => (
              <Fragment key={enc.id}>
                <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
                  {t('domain:ContentProcessing.Instance.device')} {`#${idx + 1}`}:
                </Typography>
                <Typography variant="heading-sm" noWrap>
                  {enc.model}
                </Typography>
              </Fragment>
            ))
          ) : (
            <Typography>{t('domain:ContentProcessing.Instance.noEncoderAvailable')}</Typography>
          )}
        </Panel>
        <Panel elevation={0}>
          <Typography sx={{ mb: 2, alignSelf: 'flex-start', justifySelf: 'flex-start' }} variant="heading-md">
            {t('domain:ContentProcessing.Instance.nVDecoder')}
          </Typography>
          <LockOpen sx={{ alignSelf: 'flex-start' }} />

          {avcInstance.capabilities?.hardware.nvdec?.length ? (
            avcInstance.capabilities?.hardware.nvdec?.map((dec, idx) => (
              <Fragment key={dec.id}>
                <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
                  {t('domain:ContentProcessing.Instance.device')} {`#${idx + 1}`}:
                </Typography>
                <Typography variant="heading-sm" noWrap>
                  {dec.model}
                </Typography>
              </Fragment>
            ))
          ) : (
            <Typography>{t('domain:ContentProcessing.Instance.noDecoderAvailable')}</Typography>
          )}
        </Panel>
        <Panel elevation={0}>
          <Typography
            sx={{ whiteSpace: 'nowrap', mb: 2, alignSelf: 'flex-start', justifySelf: 'flex-start' }}
            variant="heading-md"
          >
            {t('domain:ContentProcessing.Instance.supportedCodecs')}
          </Typography>
          <DataArray sx={{ alignSelf: 'flex-start' }} />

          <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
            {`${t('domain:ContentProcessing.Instance.video')}:`}
          </Typography>
          <Typography variant="heading-sm" noWrap>
            {avcInstance.capabilities?.video.codecs.join(', ')}
          </Typography>
          <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
            {`${t('domain:ContentProcessing.Instance.audio')}:`}
          </Typography>
          <Typography variant="heading-sm" noWrap>
            {avcInstance.capabilities?.audio.codecs.join(', ')}
          </Typography>
        </Panel>
        <Panel elevation={0}>
          <Typography
            sx={{ whiteSpace: 'nowrap', mb: 2, alignSelf: 'flex-start', justifySelf: 'flex-start' }}
            variant="heading-md"
          >
            {t('domain:ContentProcessing.Instance.supportedPresets')}
          </Typography>
          <Difference sx={{ alignSelf: 'flex-start' }} />

          <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
            {`${t('domain:ContentProcessing.Instance.video')}:`}
          </Typography>
          <Typography variant="heading-sm" noWrap>
            {avcInstance.capabilities?.video.presets.join(', ')}
          </Typography>
        </Panel>
      </Box>

      <Typography variant="heading-lg">{t('domain:ContentProcessing.Instance.networking')}</Typography>

      <Box sx={{ mb: 4 }}>
        <Networking instanceId={instanceId} />
      </Box>

      {(!!sdiDevices.length ||
        !!avcInstance.capabilities?.avio.ndi?.length ||
        !!avcInstance.capabilities?.avio.directories?.length) && (
        <>
          <Typography variant="heading-lg">{t('domain:ContentProcessing.Instance.audioVideoInOut')}</Typography>

          {!!sdiDevices?.length && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 1,
                mb: -1,
              }}
            >
              {sdiDevices.map((sdi) => (
                <SdiPanel key={sdi.id} {...sdi} />
              ))}
            </Box>
          )}

          {!!avcInstance.capabilities?.avio.ndi?.length && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                gap: 1,
                mb: -1,
              }}
            >
              {avcInstance.capabilities?.avio.ndi.map((ndi) => <AVIOPanel key={ndi.name} ndi={ndi} />)}
            </Box>
          )}

          {!!avcInstance.capabilities?.avio.directories?.length && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {avcInstance.capabilities?.avio.directories.map((directory) => (
                <AVIOPanel key={directory.id} directory={directory} />
              ))}
            </Box>
          )}

          <Typography sx={{ mt: 4, mb: 2 }} variant="heading-sm">
            {`${t('domain:ContentProcessing.Instance.quickCopy')}:`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <CopyToClipboard
              key={avcInstance.instanceId}
              text={avcInstance.instanceId || ''}
              onCopy={() => enqueueSnackbar(t('copiedToClipboard'), { variant: 'info' })}
            >
              <Button variant="contained" size="medium" color="primary" startIcon={<CopyAll />}>
                AV-Coder-ID
              </Button>
            </CopyToClipboard>
          </Box>
        </>
      )}
    </Box>
  )
}

export const Dashboard = memo(DashboardRaw)
